/**
 * Device speech for when the speak endpoint can't be used (an older backend, an outage): the
 * `@capacitor-community/text-to-speech` plugin in the app, `speechSynthesis` on the web. Its output level
 * is synthetic (the orb still breathes with the voice).
 */
import { Capacitor } from '@capacitor/core'
import { isNativePlatform } from '@/utils/platform'

export interface DeviceSpeech {
    available(): boolean
    /** Resolves when the line has been spoken (or failed). */
    speak(text: string, lang: string): Promise<void>
    stop(): void
    readonly speaking: boolean
}

export function createDeviceSpeech(): DeviceSpeech {
    let speaking = false
    let cancelled = 0
    const native = isNativePlatform() && Capacitor.isPluginAvailable('TextToSpeech')
    const web = typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined'

    return {
        available: () => native || web,
        get speaking() {
            return speaking
        },
        async speak(text, lang) {
            const token = cancelled
            speaking = true
            try {
                if (native) {
                    const { TextToSpeech } = await import('@capacitor-community/text-to-speech')
                    if (token !== cancelled) return
                    // No `category`: a playback-only session would turn the call's mic off mid-call. (The plugin's iOS
                    // side speaks in its own audio session and ignores it today; this keeps a future version from
                    // switching the phone to playback-only under the recognizer.)
                    await TextToSpeech.speak({ text, lang, rate: 1.0, pitch: 1.0, volume: 1.0 })
                } else if (web) {
                    await new Promise<void>((resolve) => {
                        const u = new SpeechSynthesisUtterance(text)
                        u.lang = lang
                        u.rate = 1.03
                        u.onend = () => resolve()
                        u.onerror = () => resolve()
                        window.speechSynthesis.speak(u)
                        // Some engines never fire `end`: don't hang the call on them.
                        setTimeout(resolve, 1500 + text.length * 90)
                    })
                }
            } catch {
                /* say nothing rather than break the call */
            } finally {
                if (token === cancelled) speaking = false
            }
        },
        stop() {
            cancelled += 1
            speaking = false
            try {
                if (native) void import('@capacitor-community/text-to-speech').then(({ TextToSpeech }) => TextToSpeech.stop()).catch(() => {})
                else if (web) window.speechSynthesis.cancel()
            } catch {
                /* nothing to stop */
            }
        },
    }
}
